class ClubNaviProfileStorage {
  static key = "club-navi:profile";
  static save(profile, persistent) {
    const json = JSON.stringify(profile);
    if (persistent) {
      localStorage.setItem(this.key, json);
      sessionStorage.removeItem(this.key);
    } else {
      sessionStorage.setItem(this.key, json);
    }
  }
  static load() {
    const raw = localStorage.getItem(this.key) || sessionStorage.getItem(this.key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }
}

class ClubNaviAnalytics {
  // GA4 (gtag/dataLayer vía app Google & YouTube) y Clarity; todo con guardas,
  // si alguno no está cargado simplemente no hace nada.
  static track(name, params = {}) {
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", name, params);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: name, ...params });
      }
      if (typeof window.clarity === "function") {
        window.clarity("event", name);
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) window.clarity("set", `${name}_${key}`, String(value));
        });
      }
    } catch (error) {
      /* la analítica nunca debe romper el quiz */
    }
  }
}

class ClubNaviRoutinePicker {
  static hash(text) {
    let h = 0;
    for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
    return h;
  }

  // Cada paso lista TODOS los productos disponibles de la tienda como carrusel.
  // Orden: primero la recomendada (determinista por preocupación + piel + paso:
  // pieles distintas abren con productos distintos, la misma persona siempre ve
  // lo mismo); luego en stock > compatible con tu piel (+3) > coincide con la
  // preocupación (+2) > piel exacta (+1) > stock holgado > precio.
  // Lo agotado va al final.
  static pick(routineEl, concernKey, concernTag, skinValue) {
    const skinTag = skinValue ? `Piel: ${skinValue}` : null;
    const universalTag = "Piel: Todo tipo";
    routineEl.querySelectorAll("[data-routine-step]").forEach((stepEl) => {
      const track = stepEl.querySelector(".club-navi-routine__step-products");
      const cards = Array.from(stepEl.querySelectorAll(".club-navi-routine-card"));
      cards.forEach((card) => card.closest(".col").classList.remove("is-pick"));
      if (!cards.length || !track) return;

      const tagsOf = (c) => c.dataset.tags.split("|");
      const inStock = (c) => (c.dataset.available === "true" ? 1 : 0);
      const comfy = (c) => (inStock(c) && Number(c.dataset.stock) > 2 ? 1 : 0);
      // Compatibilidad con la piel pesa MÁS que la preocupación: un producto
      // etiquetado solo para otras pieles (ej. BHA para piel grasa) nunca gana a
      // uno compatible. Sin etiquetas "Piel:" o con "Todo tipo" sirve a cualquiera.
      const score = (c) => {
        const tags = tagsOf(c);
        const hasSkinTags = tags.some((t) => t.startsWith("Piel: "));
        const exact = skinTag && tags.includes(skinTag);
        const compatible = !skinTag || exact || tags.includes(universalTag) || !hasSkinTags;
        const byConcern = concernTag && tags.includes(concernTag) ? 2 : 0;
        return (compatible ? 3 : 0) + byConcern + (exact ? 1 : 0);
      };

      const live = cards.filter((c) => inStock(c));
      let pool = live.length ? live : cards;
      const best = Math.max(...pool.map(score));
      pool = pool.filter((c) => score(c) === best);
      const stocked = pool.filter((c) => comfy(c));
      if (stocked.length) pool = stocked;
      const prices = pool.map((c) => Number(c.dataset.price)).sort((a, b) => a - b);
      const median = prices[Math.floor(prices.length / 2)];
      const band = pool.filter((c) => Number(c.dataset.price) <= median * 1.6);
      if (band.length) pool = band;

      pool.sort((a, b) => a.dataset.handle.localeCompare(b.dataset.handle));
      const chosen = pool[this.hash(`${concernKey}|${skinValue || ""}|${stepEl.dataset.clubNaviStep}`) % pool.length];

      const rest = cards
        .filter((c) => c !== chosen)
        .sort(
          (a, b) =>
            inStock(b) - inStock(a) ||
            score(b) - score(a) ||
            comfy(b) - comfy(a) ||
            Number(a.dataset.price) - Number(b.dataset.price) ||
            a.dataset.handle.localeCompare(b.dataset.handle)
        );
      [chosen, ...rest].forEach((card) => track.appendChild(card.closest(".col")));
      chosen.closest(".col").classList.add("is-pick");
      track.scrollLeft = 0;
      ClubNaviCarousel.enhance(stepEl, track);
      requestAnimationFrame(() => track.updateArrows && track.updateArrows());
    });
  }

  static pickedCards(routineEl) {
    return Array.from(routineEl.querySelectorAll(".is-pick .club-navi-routine-card"));
  }
}

class ClubNaviCarousel {
  // Fila horizontal con snap; arrastrable con mouse, swipe nativo en móvil y
  // flechas en escritorio. Idempotente: se puede llamar tras reordenar.
  static enhance(stepEl, track) {
    if (track.dataset.carousel === "ready") return;
    track.dataset.carousel = "ready";

    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse") return;
      down = true;
      moved = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });
    window.addEventListener("pointermove", (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 5) {
        moved = true;
        track.classList.add("is-dragging");
      }
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener("pointerup", () => {
      if (!down) return;
      down = false;
      track.classList.remove("is-dragging");
    });
    track.addEventListener(
      "click",
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      true
    );

    const makeArrow = (dir) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `club-navi-carousel__arrow club-navi-carousel__arrow--${dir}`;
      btn.setAttribute("aria-label", dir === "prev" ? "Anterior" : "Siguiente");
      btn.addEventListener("click", () => {
        const card = track.querySelector(".col");
        const step = card ? card.getBoundingClientRect().width + 16 : 240;
        track.scrollBy({ left: dir === "prev" ? -step : step, behavior: "smooth" });
      });
      return btn;
    };
    const wrap = document.createElement("div");
    wrap.className = "club-navi-carousel";
    track.parentNode.insertBefore(wrap, track);
    wrap.appendChild(makeArrow("prev"));
    wrap.appendChild(track);
    wrap.appendChild(makeArrow("next"));

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      wrap.classList.toggle("can-prev", track.scrollLeft > 2);
      wrap.classList.toggle("can-next", track.scrollLeft < max);
    };
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    requestAnimationFrame(update);
    track.updateArrows = update;
  }
}

class ClubNaviQuizElement extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.answers = {};
    this.isLoggedIn = this.dataset.loggedIn === "true";
    this.selectors = {
      steps: "[data-quiz-step]",
      options: "[data-answer-for]",
      progressDots: "[data-progress-dot]",
      result: ".club-navi-quiz__result",
      routines: "[data-routine-for]",
      retakeBtn: ".club-navi-quiz__retake",
      addRoutineBtn: ".club-navi-quiz__add-routine",
    };
    this.started = false;
  }

  connectedCallback() {
    this.querySelectorAll(this.selectors.options).forEach((button) => {
      button.addEventListener("click", this.onOptionClick.bind(this), {
        signal: this.abortController.signal,
      });
    });
    const addRoutineBtn = this.querySelector(this.selectors.addRoutineBtn);
    if (addRoutineBtn) {
      addRoutineBtn.addEventListener("click", this.onAddRoutine.bind(this), {
        signal: this.abortController.signal,
      });
    }
    const retakeBtn = this.querySelector(this.selectors.retakeBtn);
    if (retakeBtn) {
      retakeBtn.addEventListener("click", this.reset.bind(this), {
        signal: this.abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onOptionClick(event) {
    const button = event.currentTarget;
    const step = button.dataset.answerFor;
    const value = button.dataset.value;
    const label = button.dataset.label;

    this.answers[step] = { value, label, tag: button.dataset.concernTag || null };
    if (!this.started) {
      this.started = true;
      ClubNaviAnalytics.track("club_navi_quiz_start");
    }
    ClubNaviAnalytics.track("club_navi_quiz_answer", { step: Number(step), answer: label });

    this.querySelectorAll(`[data-answer-for="${step}"]`).forEach((el) => {
      el.classList.toggle("is-selected", el === button);
    });

    if (step === "1") {
      this.goToStep(2);
    } else if (step === "2") {
      this.showResult();
    }
  }

  goToStep(stepNumber) {
    this.querySelectorAll(this.selectors.steps).forEach((stepEl) => {
      stepEl.hidden = stepEl.dataset.quizStep !== String(stepNumber);
    });
    this.querySelectorAll(this.selectors.progressDots).forEach((dot) => {
      dot.classList.toggle("is-active", Number(dot.dataset.progressDot) <= stepNumber);
    });
  }

  showResult() {
    this.querySelectorAll(this.selectors.steps).forEach((stepEl) => {
      stepEl.hidden = true;
    });

    const concern = this.answers["1"];
    const skinType = this.answers["2"];
    if (!concern) return;

    const poolEl = this.querySelector("[data-routine-pool] .club-navi-routine");
    if (poolEl) {
      ClubNaviRoutinePicker.pick(poolEl, concern.value, concern.tag, skinType ? skinType.value : null);
    }

    const resultEl = this.querySelector(this.selectors.result);
    if (resultEl) resultEl.hidden = false;
    this.classList.add("is-result");
    const addRoutineBtn = this.querySelector(this.selectors.addRoutineBtn);
    if (addRoutineBtn) addRoutineBtn.hidden = false;

    ClubNaviAnalytics.track("club_navi_quiz_complete", {
      concern: concern.label,
      skin_type: skinType ? skinType.label : null,
    });
    this.saveProfile(concern, skinType);
  }

  async onAddRoutine(event) {
    const button = event.currentTarget;
    const concern = this.answers["1"];
    if (!concern) return;
    const routineEl = this.querySelector("[data-routine-pool] .club-navi-routine");
    if (!routineEl) return;
    const items = ClubNaviRoutinePicker.pickedCards(routineEl)
      .filter((card) => card.dataset.available === "true")
      .map((card) => ({ id: Number(card.dataset.variantId), quantity: 1 }));
    if (!items.length) return;

    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
    button.disabled = true;
    ClubNaviAnalytics.track("club_navi_quiz_add_routine", { concern: concern.label, items: items.length });
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error("cart/add failed");
      window.location.href = `${root}cart`;
    } catch (error) {
      button.disabled = false;
    }
  }

  saveProfile(concern, skinType) {
    const profile = {
      skin_type: skinType ? skinType.value : null,
      skin_concern: concern.label,
      routine_collection: concern.value,
      quiz_completed_at: new Date().toISOString(),
    };
    ClubNaviProfileStorage.save(profile, this.isLoggedIn);
  }

  reset() {
    this.answers = {};
    this.querySelectorAll(this.selectors.options).forEach((el) => {
      el.classList.remove("is-selected");
    });
    this.querySelectorAll(this.selectors.routines).forEach((el) => {
      el.hidden = true;
    });
    const resultEl = this.querySelector(this.selectors.result);
    if (resultEl) {
      resultEl.hidden = true;
    }
    this.classList.remove("is-result");
    const addRoutineBtn = this.querySelector(this.selectors.addRoutineBtn);
    if (addRoutineBtn) addRoutineBtn.hidden = true;
    this.started = false;
    this.goToStep(1);
  }
}

customElements.define("club-navi-quiz", ClubNaviQuizElement);
